import type { JSX } from 'react';
import type { ClientResource } from '@majuscule/contracts';
import { useQuery } from '@tanstack/react-query';

import { PricingRulesPanel } from './pricing-rules-panel';
import { formatMarginShare, UseCaseStatusBadge, useSmsServiceCost } from './shared';
import { SmsSettingsPanel } from './sms-settings-panel';
import { UseCaseInvoiceCard } from '../UseCaseInvoiceCard';
import { internalFetch } from '../../lib/api';
import {
  applicableRule,
  describeRule,
  EXECUTION_MODE_LABELS,
  formatMicroEur,
  KIND_LABELS,
  type PricingRule,
  pricePerSms,
  RULE_SOURCE_LABELS,
  smsSettingsOf,
  type UseCase,
} from '../../lib/use-cases';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHead } from '@/components/ui/card';
import { HBars } from '@/components/ui/chart';
import { Kv, KvItem } from '@/components/ui/kv';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHead } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

/** Selling price, purchase cost and margin of one SMS, in € HT. */
function SmsEconomics({
  modelId,
  priceMicroEur,
}: {
  modelId: string;
  priceMicroEur: number;
}): JSX.Element | null {
  const cost = useSmsServiceCost(modelId);
  if (cost.status !== 'ok' || cost.costMicroEur === null) {
    return null;
  }
  const margin = priceMicroEur - cost.costMicroEur;
  const share = formatMarginShare(margin, priceMicroEur);
  return (
    <Card>
      <CardHead
        as="h3"
        title="Économie par SMS"
        description="En € HT, prix de vente moins coût d’achat"
      />
      <CardContent>
        <HBars
          ariaLabel={`Prix de vente ${formatMicroEur(priceMicroEur)}, coût d’achat ${formatMicroEur(cost.costMicroEur)}, marge ${formatMicroEur(margin)} par SMS, soit ${share}.`}
          max={Math.max(priceMicroEur, cost.costMicroEur)}
          items={[
            {
              key: 'price',
              name: 'Prix de vente',
              value: priceMicroEur,
              label: formatMicroEur(priceMicroEur),
              color: 'chart-2',
            },
            {
              key: 'cost',
              name: 'Coût d’achat',
              value: cost.costMicroEur,
              label: formatMicroEur(cost.costMicroEur),
              color: 'chart-5',
            },
            {
              key: 'margin',
              name: `Marge (${share})`,
              value: Math.max(margin, 0),
              label: formatMicroEur(margin),
              color: 'chart-3',
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}

/** « Aperçu » tab: what the use case is and the price its owner is billed today. */
function Overview({
  useCase,
  clientName,
  modelName,
}: {
  useCase: UseCase;
  clientName: string;
  modelName: string;
}): JSX.Element {
  // Same query as the pricing panel: one request, shared cache.
  const rules = useQuery({
    queryKey: ['pricing-rules', useCase.id],
    queryFn: () =>
      internalFetch<{ rules: PricingRule[] }>(`/internal/use-cases/${useCase.id}/pricing-rules`),
  });
  const applied =
    rules.data === undefined
      ? null
      : applicableRule(rules.data.rules, useCase.ownerClientId, Date.now());
  const smsPrice = applied === null ? null : pricePerSms(applied.rule.rule);

  return (
    <>
      <Kv>
        <KvItem label="Type">{KIND_LABELS[useCase.kind]}</KvItem>
        <KvItem label="Mode">{EXECUTION_MODE_LABELS[useCase.executionMode]}</KvItem>
        <KvItem label={useCase.kind === 'sms' ? 'Service' : 'Modèle'}>{modelName}</KvItem>
        <KvItem label="Client propriétaire">{clientName}</KvItem>
        <KvItem label="Prix appliqué au client" mono>
          {rules.isLoading
            ? '…'
            : applied === null
              ? 'Aucune règle en vigueur'
              : `${describeRule(applied.rule.rule)} HT`}
          {applied !== null ? (
            <span className="block font-sans text-caption text-muted-foreground">
              {RULE_SOURCE_LABELS[applied.source]}
            </span>
          ) : null}
        </KvItem>
        {useCase.kind === 'sms' ? null : (
          <>
            <KvItem label="Fichiers">
              {useCase.acceptsFiles ? 'Acceptés (PDF, images)' : 'Non acceptés'}
            </KvItem>
            <KvItem label="Longueur maximale" mono>
              {useCase.params.maxOutputTokens === null
                ? '—'
                : `${useCase.params.maxOutputTokens} tokens`}
            </KvItem>
            <KvItem label="Température" mono>
              {useCase.params.temperature === null
                ? '—'
                : String(useCase.params.temperature).replace('.', ',')}
            </KvItem>
          </>
        )}
      </Kv>
      {useCase.kind === 'sms' && smsPrice !== null ? (
        <SmsEconomics modelId={useCase.modelId} priceMicroEur={smsPrice} />
      ) : null}
    </>
  );
}

/**
 * Side sheet of a use case (UseCaseSheet.dc.html): overview, SMS settings (SMS
 * only), pricing rules and invoice labels in tabs; duplicate and delete or
 * reactivate in the footer.
 */
export function UseCaseSheet({
  useCase,
  clients,
  clientName,
  modelName,
  onClose,
  onDuplicate,
  onDelete,
  onReactivate,
  reactivating,
}: {
  useCase: UseCase | null;
  clients: readonly ClientResource[];
  clientName: (clientId: string | null) => string;
  modelName: (modelId: string) => string;
  onClose: () => void;
  onDuplicate: (useCase: UseCase) => void;
  onDelete: (useCase: UseCase) => void;
  onReactivate: (useCase: UseCase) => void;
  reactivating: boolean;
}): JSX.Element {
  return (
    <Sheet
      open={useCase !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <SheetContent aria-describedby={undefined}>
        {useCase === null ? null : (
          // Keyed by use case: the tab and the forms start over for another one.
          <SheetPanels
            key={useCase.id}
            useCase={useCase}
            clients={clients}
            clientName={clientName}
            modelName={modelName}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onReactivate={onReactivate}
            reactivating={reactivating}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function SheetPanels({
  useCase,
  clients,
  clientName,
  modelName,
  onDuplicate,
  onDelete,
  onReactivate,
  reactivating,
}: {
  useCase: UseCase;
  clients: readonly ClientResource[];
  clientName: (clientId: string | null) => string;
  modelName: (modelId: string) => string;
  onDuplicate: (useCase: UseCase) => void;
  onDelete: (useCase: UseCase) => void;
  onReactivate: (useCase: UseCase) => void;
  reactivating: boolean;
}): JSX.Element {
  const sms = smsSettingsOf(useCase);
  const owner = clientName(useCase.ownerClientId);
  const model = modelName(useCase.modelId);
  const description = [
    KIND_LABELS[useCase.kind],
    owner,
    sms !== null ? `expéditeur ${sms.sender}` : model,
  ].join(', ');

  return (
    <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
      <SheetHead
        title={useCase.key}
        mono
        badge={<UseCaseStatusBadge useCase={useCase} />}
        description={description}
      >
        <TabsList aria-label="Sections de la fiche">
          <TabsTrigger value="overview">Aperçu</TabsTrigger>
          {useCase.kind === 'sms' ? <TabsTrigger value="sms">Réglages SMS</TabsTrigger> : null}
          <TabsTrigger value="price">Règles de prix</TabsTrigger>
          <TabsTrigger value="invoice">Facture</TabsTrigger>
        </TabsList>
      </SheetHead>
      <SheetBody>
        <TabsContent value="overview" className="m-0 grid gap-6">
          <Overview useCase={useCase} clientName={owner} modelName={model} />
        </TabsContent>
        {useCase.kind === 'sms' ? (
          <TabsContent value="sms" className="m-0 grid gap-6">
            <SmsSettingsPanel useCase={useCase} />
          </TabsContent>
        ) : null}
        <TabsContent value="price" className="m-0 grid gap-6">
          <PricingRulesPanel useCase={useCase} clients={clients} clientName={clientName} />
        </TabsContent>
        <TabsContent value="invoice" className="m-0 grid gap-6">
          <UseCaseInvoiceCard useCase={useCase} />
        </TabsContent>
      </SheetBody>
      <SheetFooter>
        <Button
          type="button"
          variant="outline"
          className="mr-auto"
          onClick={() => {
            onDuplicate(useCase);
          }}
        >
          Dupliquer
        </Button>
        {useCase.active ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onDelete(useCase);
            }}
          >
            Supprimer
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            disabled={reactivating}
            onClick={() => {
              onReactivate(useCase);
            }}
          >
            Réactiver
          </Button>
        )}
      </SheetFooter>
    </Tabs>
  );
}
